import { useState } from 'react';
import { Box, List, ListItem } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Draggable as DraggableIcon } from '@brainpool/icon-pack';

type Item = {
  id: string;
  [key: string]: any;
};

export interface DraggableProps<T extends Item> {
  items: T[];
  setItems: (items: T[]) => void;
  renderItem: (item: T) => React.ReactNode;
  type?: 'grid' | 'list';
  gridCols?: number;
}

export function Draggable<T extends Item>({
  items,
  setItems,
  renderItem,
  type = 'list',
  gridCols = 3,
}: DraggableProps<T>) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => () => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (index: number) => () => {
    if (draggedIndex === null || draggedIndex === index) return;

    const updatedItems = [...items];
    const [movedItem] = updatedItems.splice(draggedIndex, 1);
    updatedItems.splice(index, 0, movedItem);

    setItems(updatedItems);
    setDraggedIndex(null);
  };

  const getDragProps = (index: number) => ({
    draggable: true,
    onDragStart: handleDragStart(index),
    onDragOver: handleDragOver,
    onDrop: handleDrop(index),
  });

  if (type === 'grid') {
    return (
      <Grid container spacing={2}>
        {items.map((item, index) => (
          <Grid size={Math.max(1, Math.floor(12 / gridCols))} key={item.id}>
            <Box sx={{ position: 'relative' }} {...getDragProps(index)}>
              <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                <DraggableIcon size={16} fill={'info'} />
              </Box>
              {renderItem(item)}
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  // List layout
  return (
    <List>
      {items.map((item, index) => (
        <ListItem key={item.id} {...getDragProps(index)} sx={{ position: 'relative', p: 2 }}>
          <Box sx={{ position: 'absolute', top: '1rem', right: '1rem' }}>
            <DraggableIcon size={16} fill={'info'} />
          </Box>
          {renderItem(item)}
        </ListItem>
      ))}
    </List>
  );
}

export default Draggable;
